import gql from 'graphql-tag';
import moment from 'moment';
import {
  META_NAMESPACE,
  META_SHIP_DATE_KEY,
  META_STATUS_KEY,
  Manufacturer,
  SENT_TO_MAN_TAG,
} from '../../utils/types';
import {
  getStatusMessage,
  isSentToAllManufacturers,
} from '../../utils/order/orderStatus';
import { normalizeShipDateObjectDate } from '../../utils/order/orderData';
import OrderEditableFieldsFragment from './fragments/OrderEditableFields';
import { Metafield } from '../../models/metafield';
import { CustomAttribute } from '../../models/customAttribute';
import { ShipDate } from '../../models/shipDate';
import { OrderStatus } from '../../models/orderStatus';
import { OrderData } from "./getOrderData";

export const updOrderMetafieldsMutation = gql`
  ${OrderEditableFieldsFragment}
  mutation orderUpdate($input: OrderInput!) {
    orderUpdate(input: $input) {
      order {
        ...OrderEditableFields
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export type UpdateOrderMetafieldsResponse = {
  orderUpdate: {
    order: OrderData
    userErrors: {
      field: string;
      message: string;
    }
  }
}

type UpdateOrderStatusOpts = {
  orderId: string;
  statusObject: OrderStatus;
  manufacturers: Manufacturer[];
  metaId?: string;
  oldAttributes?: CustomAttribute[];
  oldTags?: string[];
};

export const updOrderStatusOpts = ({
  orderId,
  statusObject,
  manufacturers,
  metaId,
  oldAttributes,
  oldTags,
}: UpdateOrderStatusOpts) => {
  const statusMetafield: Metafield = {
    id: metaId,
    key: META_STATUS_KEY,
    namespace: META_NAMESPACE,
    type: 'json',
    value: JSON.stringify(statusObject),
  };

  const title = 'Send to';
  const sendAttrs = Object.keys(statusObject).map((man: Manufacturer) => ({
    key: `${title} ${man.toUpperCase()}`,
    value: getStatusMessage(statusObject[man].status),
  }));

  const attrs = (oldAttributes || []).filter(attr => !attr.key.includes(title));
  attrs.push(...sendAttrs);

  // if all manufacturers are sent to, add tag
  const tags = isSentToAllManufacturers(statusObject)
    ? [SENT_TO_MAN_TAG, ...oldTags]
    : oldTags;

  return {
    variables: {
      input: {
        id: `gid://shopify/Order/${orderId}`,
        customAttributes: attrs,
        tags,
        metafields: [statusMetafield],
      },
    },
  };
};

type UpdateOrderShipDateOpts = {
  orderId: string;
  shipDateObject: ShipDate;
  metaId?: string;
  oldAttributes?: CustomAttribute[];
  oldTags?: string[];
};

export const updOrderShipDateOpts = ({
  orderId,
  shipDateObject,
  metaId,
  oldAttributes,
  oldTags,
}: UpdateOrderShipDateOpts) => {
  const dateMetafield: Partial<Metafield> = {
    id: metaId,
    key: META_SHIP_DATE_KEY,
    namespace: META_NAMESPACE,
    type: 'json',
    value: JSON.stringify(shipDateObject),
  };

  const title = 'Delivery date';
  const dateLabel = moment(
    normalizeShipDateObjectDate(shipDateObject, true).date,
  ).format('MMMM Do, YYYY');

  const attrs = (oldAttributes || []).filter(attr => attr.key !== title);
  attrs.push({
    key: title,
    value: shipDateObject.sent ? `${dateLabel} (sent to customer)` : dateLabel,
  });

  const tags = oldTags || [];

  return {
    variables: {
      input: {
        id: `gid://shopify/Order/${orderId}`,
        customAttributes: attrs,
        tags,
        metafields: [dateMetafield],
      },
    },
  };
};
